import { useField } from 'formik';
import React from 'react';

import styled from '@emotion/styled';

import ChevronFormDown from '../Icons/ChevronFormDown';
import { capitalize } from '../../utils/utils';

export interface IInputField {
  label?: any;
  id?: string;
  name: string;
  type: string;
  placeholder?: string;
  rpiSrc?: string;
  lpiSrc?: string;
  children?: any;
  className?: string;
  readonly?: boolean;
  min?: string;
  max?: string;
  onValueChange?: (value: string, fieldName: string) => void;
}

export const SelectField: React.FC<
  Omit<IInputField, 'placeholder' | 'type'>
> = ({ label, className, ...props }) => {
  const [field, meta] = useField(props);
  return (
    <InputWrapper className={className}>
      <InputLabel htmlFor={props.name || props.id}>{label}</InputLabel>
      <InputContainer>
        <Select {...field} {...props} />
        <SelectDropDownIcon>
          <ChevronFormDown />
        </SelectDropDownIcon>
      </InputContainer>
      {meta.touched && meta.error ? (
        <FieldErrorInfo>{capitalize(meta.error)}</FieldErrorInfo>
      ) : null}
    </InputWrapper>
  );
};

export const TextInputField: React.FC<IInputField> = ({
  label,
  lpiSrc,
  rpiSrc,
  className,
  readonly = false,
  onValueChange,
  ...props
}) => {
  const [field, meta] = useField(props);

  return (
    <InputWrapper className={className}>
      {label && (
        <InputLabel htmlFor={props.name || props.id}>{label}</InputLabel>
      )}
      <InputContainer>
        <Input
          {...field}
          {...props}
          readOnly={readonly}
          className={className}
          onChange={(event) => {
            field.onChange(event);
            onValueChange?.(event.target.value, props.name);
          }}
        />
        {lpiSrc ? <LeftPlaceHolderCardIcon alt='icon' src={lpiSrc} /> : null}
        {rpiSrc ? <RightPlaceHolderCardIcon alt='icon' src={rpiSrc} /> : null}
      </InputContainer>
      {meta.touched && meta.error ? (
        <FieldErrorInfo>{capitalize(meta.error)}</FieldErrorInfo>
      ) : null}
    </InputWrapper>
  );
};

export const TextAreaInputField: React.FC<IInputField> = ({
  label,
  lpiSrc,
  rpiSrc,
  className,
  ...props
}) => {
  const [field, meta] = useField(props);

  return (
    <InputWrapper className={className}>
      <InputLabel htmlFor={props.name || props.id}>{label}</InputLabel>
      <InputContainer>
        <TextArea {...field} {...props} />
        {lpiSrc ? <LeftPlaceHolderCardIcon alt='icon' src={lpiSrc} /> : null}
        {rpiSrc ? <RightPlaceHolderCardIcon alt='icon' src={rpiSrc} /> : null}
      </InputContainer>
      {meta.touched && meta.error ? (
        <FieldErrorInfo>{capitalize(meta.error)}</FieldErrorInfo>
      ) : null}
    </InputWrapper>
  );
};

export const Checkbox: React.FC<
  Pick<IInputField, 'name' | 'id' | 'children' | 'className'>
> = ({ children, className, ...props }) => {
  const [field, meta] = useField({ ...props, type: 'checkbox' });
  return (
    <CheckboxWrapper className={className}>
      <label className='checkbox'>
        <input type='checkbox' {...field} {...props} />
        {children}
      </label>
      {meta.touched && meta.error ? (
        <div className='error'>{capitalize(meta.error)}</div>
      ) : null}
    </CheckboxWrapper>
  );
};

const CheckboxWrapper = styled.div`
  display: flex;

  font-size: 16px;
  font-weight: 500;

  & > label {
    display: flex;
    align-items: center;
  }

  & > label > input[type='checkbox'] {
    color: red;
    margin-right: 10px; /* Adjust the value as needed to create the desired gap */
  }

  & > label {
    color: black;
  }

  & > label > input:checked {
    color: red;
    background-color: red;
    border-color: #4caf50;
  }
`;

export const FieldErrorInfo = styled.div`
  font-size: 12px;
  color: red;
  margin-top: -10px;
`;

// export const ErrorContentSpaceHolder = styled.div`
//   height: 8px;
// `;

export const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const InputLabel = styled.label`
  font-weight: 500;
  font-size: 12px;
  line-height: 12px;
  color: black;
  user-select: none;
`;

interface IInput {
  textAlign?: string;
  lpiSrc?: string;
  rpiSrc?: boolean;
}

export const Input = styled.input<IInput>`
  border: 1px solid ${({ theme }) => theme.palette.darkBorderColor};
  border-radius: 12px;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
  padding: 10px 16px;
  text-align: ${({ textAlign }) => (textAlign ? textAlign : 'left')};
  font-weight: 400;
  line-height: 14px;
  font-size: 14px;
  height: 40px;
  cursor: text;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.palette.HetheraPrimary};
  }
  &:hover {
    cursor: text;
  }

  &::placeholder {
    color: ${({ theme }) => theme.palette.darkestBorderColor};
    opacity: 1;
    font-weight: 400;
  }
`;

const TextArea = styled.textarea<IInput>`
  border: 1px solid ${({ theme }) => theme.palette.darkBorderColor};
  border-radius: 12px;
  padding: ${({ lpiSrc, rpiSrc }) => (lpiSrc || rpiSrc ? '5rem' : '1rem')};
  text-align: ${({ textAlign }) => (textAlign ? textAlign : 'left')};

  font-size: 14px;
  font-weight: 400;
  resize: none;
  cursor: text;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.palette.HetheraPrimary};
  }
  &:hover {
    cursor: text;
  }

  &::placeholder {
    color: ${({ theme }) => theme.palette.darkestBorderColor};
    opacity: 1;
  }
`;

export const Select = styled.select`
  border: 1px solid ${({ theme }) => theme.palette.darkBorderColor};
  border-radius: 12px;
  padding: 0rem 1rem 0rem 1rem;
  font-weight: 400;
  font-size: 1rem;
  height: 48px;
  /* width: 100%; */

  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-color: white !important;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.palette.borderColor};
  }
  &:hover {
    cursor: pointer;
  }

  &:has(option:checked:disabled) {
    color: ${({ theme }) => theme.palette.borderColor};
  }

  & > option {
    background-color: white !important;
    color: black !important;
  }

  @media (prefers-color-scheme: dark) {
    & {
      color: black;
      background: white !important;
    }

    & > option {
      background-color: white !important;
      color: black !important;
    }
  }
`;

export const InputContainer = styled.div`
  display: grid;
  height: fit-content;

  & > * {
    grid-column: 1;
    grid-row: 1;
  }
  &:hover {
    cursor: pointer;
  }
`;

export const LeftPlaceHolderCardIcon = styled.img`
  height: 1.5rem;
  display: grid;
  margin-left: 0.8rem;
  margin-top: 0.8rem;
`;

export const RightPlaceHolderCardIcon = styled.img`
  height: 1.5rem;
  display: grid;
  margin-right: 1rem;
  margin-left: auto;
  margin-top: 0.8rem;
`;

export const SelectDropDownIcon = styled.div`
  display: grid;
  place-content: center;
  margin-right: 12px;
  margin-left: auto;

  /* outline: 1px solid red; */
`;
